import { FilterModel } from '@hooks/useFiltersSearchParams';
import { PaginationModel } from '@hooks/usePagingSearchParams';
import { sellerApiClient, getParams } from '@api/apiClient/sellerApiClient';
import { useQuery } from 'react-query';

export interface INotificationItem {
  id: string;
  link: string;
  category: string;
  title: string;
  description: string;
  content: string;
  pubDate: string;
  isRead: boolean;
  isFavorite: boolean;
  imgUrl: string;
}

export interface NotificationsResult {
  total: number;
  items: INotificationItem[];
}

export const getNotifications = async ({
  page,
  filters,
}: {
  page: PaginationModel;
  filters?: FilterModel[];
}): Promise<NotificationsResult> => {
  const response = await sellerApiClient.get<NotificationsResult>(
    '/v1/notifications/page',
    {
      params: getParams(page, filters),
      paramsSerializer: { indexes: null },
    },
  );

  return response.data;
};

export const getNotificationDetails = async (id: string): Promise<INotificationItem> => {
  const response = await sellerApiClient.get<INotificationItem>(
    `/v1/notifications/${id}`,
  );

  return response.data;
};

export type TNotificationsUnreadCount = {
  all: number;
  important: number;
  news: number;
  offersAndPricing: number;
}

export const getNotificationsUnreadCount = async (currentLanguage: string): Promise<TNotificationsUnreadCount> => {
  const response = await sellerApiClient.get<TNotificationsUnreadCount>(
    `/v1/notifications/unread-count?Language=${currentLanguage}`,
  );

  return response.data;
};

export function useNotificationsUnreadCount(currentLanguage: string) {
  const { data, refetch, isLoading, isFetching, isFetched, isError } = useQuery({
    queryKey: ['getNotificationsUnreadCount', currentLanguage],
    queryFn: () => getNotificationsUnreadCount(currentLanguage),
    retry: 1,
  });

  return {
    isLoading,
    isFetching,
    isFetched,
    isError,
    data,
    refetch,
  };
}

export const toggleFavorite = async (id: string): Promise<void> => {
  const response = await sellerApiClient.post<void>(
    `/v1/notifications/${id}/toggle-favorite`,
  );

  return response.data;
};

export const markAsRead = async (id: string): Promise<void> => {
  const response = await sellerApiClient.post<void>(`/v1/notifications/${id}/mark-as-read`);
  return response.data;
};

export type TPagedNotificationsResult = NotificationsResult & {
  notificationId: string;
  pageNumber: number;
};

export type TPageByLinkCode = {
  linkCode: string;
  pageSize: number;
};

export const getNotificationsPageByLinkCode = async (params: TPageByLinkCode): Promise<TPagedNotificationsResult> => {
  const { linkCode, pageSize } = params;
  const response = await sellerApiClient.get<TPagedNotificationsResult>(
      `/v1/notifications/page-by-linkCode?linkCode=${linkCode}&pageSize=${pageSize}`
  );
  return response.data;
};
